import React, { useState } from 'react'
import { ChallengeService } from '@duck/api-definitions'
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Badge,
} from '@duck/ui'
import { FeedbackOverview } from '../FeedbackDialog'
import { useTar } from '@/modules'
import { formatFeedbackGrade, formatFeedbackStatus, getFeedbackStatusBadgeColor } from './utils'
import { apis } from '../../modules/apiClients'

interface ChallengeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  challenge: ChallengeService.ChallengeInfo;
  onDelete?: () => void;
}

export const ChallengeDialog: React.FC<ChallengeDialogProps> = ({
  isOpen,
  onClose,
  challenge,
  onDelete,
}) => {
  const { t } = useTar()
  const [error, setError] = useState<string | null>(null)
  const [showFeedbackOverview, setShowFeedbackOverview] = useState(false)
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const handleClose = () => {
    setError(null)
    setShowFeedbackOverview(false)
    setShowDeleteConfirmation(false)
    onClose()
  }

  const handleViewFeedback = () => {
    setShowFeedbackOverview(true)
  }

  const handleCloseFeedbackOverview = () => {
    setShowFeedbackOverview(false)
  }

  const handleDeleteClick = async () => {
    if (!showDeleteConfirmation) {
      // First click: show confirmation
      setShowDeleteConfirmation(true)
      return
    }

    // Second click: actually delete
    try {
      setIsDeleting(true)
      setError(null)
      await apis.challengeService.deleteChallenge(challenge.challenge.id)
      setShowDeleteConfirmation(false)
      if (onDelete) {
        onDelete()
      }
      handleClose()
    } catch (err) {
      const errorMessage = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Failed to delete challenge. Please try again.'
      setError(errorMessage)
      setShowDeleteConfirmation(false)
    } finally {
      setIsDeleting(false)
    }
  }


  if (!challenge) {
    return null
  }

  const isFinished = challenge.challenge.status === ChallengeService.ChallengeStatusEnum.Finished
  const hasFeedback = challenge.challenge.feedbackStatus === ChallengeService.FeedbackStatusEnum.Processed

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="text-xl">
              {t('challenges.challengeDetails')}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 overflow-y-auto max-h-[calc(90vh-200px)] pr-2 custom-scrollbar">
            {error && (
              <div className="rounded-md bg-error/10 border border-error/20 p-4">
                <div className="text-error text-sm">{error}</div>
              </div>
            )}

            {challenge && (
              <>
                {/* Challenge Status */}
                <div className="bg-muted/5 rounded-lg p-4 border border-border">
                  <h3 className="text-lg font-semibold text-foreground mb-3">{t('challenges.status')}</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">{t('challenges.status')}:</span>
                      <span className="ml-2 font-medium text-foreground">{t(`challenges.${challenge.challenge.status}`)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground mr-2">{t('challenges.feedbackStatus')}:</span>
                      <Badge className={getFeedbackStatusBadgeColor(challenge.challenge.feedbackStatus)}>
                        {t(formatFeedbackStatus(challenge.challenge.feedbackStatus))}
                      </Badge>
                    </div>
                    <div>
                      <span className="text-muted-foreground">{t('common.workTime')}:</span>
                      <span className="ml-2 font-medium text-foreground">{challenge.challenge.workTime || 0} minutes</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">{t('challenges.feedbackGrade')}:</span>
                      <span className="ml-2 font-medium text-foreground">
                        {formatFeedbackGrade(challenge.challenge.feedbackGrade)}
                      </span>
                    </div>
                  </div>
                </div>


                {/* User Information */}
                <div className="bg-muted/5 rounded-lg p-4 border border-border">
                  <h3 className="text-lg font-semibold text-foreground mb-3">{t('challenges.userInfo')}</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">{t('challenges.userId')}:</span>
                      <span className="ml-2 font-medium text-foreground">{challenge.challenge.userId}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">{t('challenges.challengeId')}:</span>
                      <span className="ml-2 font-medium text-foreground">{challenge.challenge.id}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">{t('challenges.startTime')}:</span>
                      <span className="ml-2 font-medium text-foreground">
                        {new Date(challenge.challenge.startTime).toLocaleString()}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">{t('challenges.endTime')}:</span>
                      <span className="ml-2 font-medium text-foreground">
                        {challenge.challenge.endTime ? new Date(challenge.challenge.endTime).toLocaleString() : t('common.notApplicable')}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                {isFinished && hasFeedback && (
                  <div className="flex justify-center">
                    <Button
                      onClick={handleViewFeedback}
                      className="px-6 py-2 bg-accent text-white rounded-md hover:bg-accent/90 transition-colors"
                    >
                      {t('challenges.viewFeedback')}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>

          <DialogFooter className="pt-4">
            <div className="flex justify-between w-full">
              <Button
                onClick={handleDeleteClick}
                disabled={isDeleting}
                className={showDeleteConfirmation
                  ? "bg-error hover:bg-error/90 text-white"
                  : "bg-destructive hover:bg-destructive/90 text-white"
                }
              >
                {isDeleting
                  ? 'Deleting...'
                  : showDeleteConfirmation
                    ? 'Are you sure?'
                    : t('common.delete')
                }
              </Button>
              <Button onClick={handleClose}>
                {t('common.close')}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Feedback Overview Dialog */}
      {showFeedbackOverview && challenge && (
        <FeedbackOverview
          isOpen={showFeedbackOverview}
          onClose={handleCloseFeedbackOverview}
          challenge={challenge}
        />
      )}
    </>
  )
}
